// PORTED COPY — A20 W2b. Original: TPRM7 packages/ui/src/reporter/ReportProvider.tsx
// (@vendorwatch/ui). Identical below this header EXCEPT for the removed
// `'use client'` directive: this is a Vite SPA with no RSC boundary, and Rollup
// warns on module-level directives it cannot honour. Everything else — the
// gzip ceiling, the route-only identity, the fetch fallback — is the original.
//
// A20 · W1 — the reporter context.
//
// This is the anti-corruption boundary in component form. The widget knows about
// exactly ONE endpoint that we own. It never talks to storage, never talks to
// GitHub, never learns a bucket name. That single constraint is what delivers
// three of the operator's asks at once: nothing third-party is visible to users,
// the storage location is masked because the browser only ever sees a Polestead
// origin, and swapping anything downstream later touches one adapter.

import * as React from 'react';
import { useRecorder, type Recorder } from './useRecorder';
import { makeTranslator, type Translator } from './strings';
import {
  SCHEMA_VERSION,
  type ReporterConfig,
  type ReporterMode,
  type ReportBody,
  type ReportEnvelope,
  type SubmitResult,
} from './types';

/**
 * ── THE ONE SIZE CONSTANT (A20 F4) ──────────────────────────────────────────
 *
 * The licence server is the only ceiling that actually decides whether a report
 * lands, so it is the only number stated by hand; everything here is DERIVED
 * from it. It used to be the other way round — this file believed 4 MiB of gzip
 * was fine, the hosted bridge allowed 6 MB, and the licence server's parser
 * accepted 1 MB — which meant the reports worth having most (a long session on a
 * busy DOM, ending in a crash with a full buffer) were the only ones that could
 * not be filed. They did not even fail cleanly: the licence server answers 500
 * rather than 413 on an oversized body, report-submit reads 5xx as `unreachable`
 * and parks the report in report_outbox, and the drain then re-posts it nightly
 * for ~30 days against a ceiling it can never fit under — after the dialog has
 * already told the user it was saved.
 *
 * Keep this block byte-identical with:
 *   LICENSE  backend/src/app.ts                                    (the source)
 *   TPRM7    packages/ui/src/reporter/ReportProvider.tsx
 *   LICENSE  customer-portal/src/components/reporter/transport.ts
 * report-submit's MAX_BODY_BYTES (6 MB) is NOT part of this chain: it is a
 * hostile-client egress fence in front of the licence server and only has to
 * stay above MAX_REPORT_BODY_BYTES.
 */
const MAX_REPORT_BODY_BYTES = 4 * 1024 * 1024;
/**
 * report-submit rewrites `identity` with token-derived ids on the way through,
 * so the body the licence server measures is a little larger than the one we
 * measured here. Leave it room. (This console posts straight to the licence
 * server, but the constant chain stays identical across all copies on purpose —
 * one shape, no per-app arithmetic to get wrong.)
 */
const BRIDGE_HEADROOM_BYTES = 8 * 1024;
const MAX_CLIENT_BODY_BYTES = MAX_REPORT_BODY_BYTES - BRIDGE_HEADROOM_BYTES;
/**
 * Everything that is not the recording: the user's text, the client block, and
 * the console/network buffers — which ride UNCOMPRESSED and can, on a page that
 * is throwing, be the bulk of the envelope. This is a fast pre-filter, not a
 * promise; `enforceBodyCeiling` below is what actually guarantees the fit.
 */
const ENVELOPE_RESERVE_BYTES = 512 * 1024;
/** base64 turns 3 bytes into 4. */
const BASE64_INFLATION = 4 / 3;
/**
 * Ceiling on the compressed capture. Past this we send the report WITHOUT the
 * recording rather than failing the submit — a text report that arrives beats a
 * perfect one that doesn't. The dialog says so; it is never silent.
 */
const MAX_CAPTURE_BYTES = Math.floor(
  (MAX_CLIENT_BODY_BYTES - ENVELOPE_RESERVE_BYTES) / BASE64_INFLATION,
);

interface ReporterContextValue {
  config: ReporterConfig;
  recorder: Recorder;
  locale: string;
  t: Translator;
  open: boolean;
  mode: ReporterMode;
  setOpen: (open: boolean, mode?: ReporterMode) => void;
  submit: (body: ReportBody) => Promise<SubmitResult>;
  /** True when the capture was dropped for size on the last submit. */
  lastCaptureDropped: boolean;
}

const ReporterContext = React.createContext<ReporterContextValue | null>(null);

export function useReporter(): ReporterContextValue {
  const ctx = React.useContext(ReporterContext);
  if (!ctx) {
    throw new Error('useReporter must be used inside <ReportProvider>');
  }
  return ctx;
}

/** Non-throwing variant — lets a surface render the button only when wired. */
export function useReporterOptional(): ReporterContextValue | null {
  return React.useContext(ReporterContext);
}

/** gzip via the platform. No pako, no fflate, no dependency. */
async function gzipToBase64(json: string): Promise<string | null> {
  try {
    const CS = (globalThis as { CompressionStream?: typeof CompressionStream }).CompressionStream;
    if (!CS) return null;
    const stream = new Blob([json]).stream().pipeThrough(new CS('gzip'));
    const buf = await new Response(stream).arrayBuffer();
    if (buf.byteLength > MAX_CAPTURE_BYTES) return null;
    // Chunked to avoid blowing the argument limit on large buffers.
    const bytes = new Uint8Array(buf);
    let binary = '';
    const CHUNK = 0x8000;
    for (let i = 0; i < bytes.length; i += CHUNK) {
      binary += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
    }
    return btoa(binary);
  } catch {
    return null;
  }
}

function currentRoute(): string {
  if (typeof location === 'undefined') return '';
  // Path only — query strings carry ring tokens and reset tokens.
  return location.pathname || '/';
}

/**
 * Exact wire size. `String.length` counts UTF-16 units, but the server counts
 * bytes, and a description written in CJK or emoji is 3–4 bytes per unit.
 */
function wireBytes(s: string): number {
  try {
    return new TextEncoder().encode(s).byteLength;
  } catch {
    return s.length;
  }
}

type WirePayload = ReportEnvelope & { captureGzipB64?: string };

/**
 * Make the body fit, shedding the least valuable part first, and say whether
 * anything was shed.
 *
 * MAX_CAPTURE_BYTES is only a pre-filter; THIS is the guarantee, and it measures
 * the real serialized body rather than estimating it. The reserve cannot bound
 * the console buffer on its own — 200 entries × 10 args × 1 000 chars is
 * megabytes, and a page that is throwing is exactly where that happens, which is
 * exactly the report we must not lose.
 */
function enforceBodyCeiling(
  envelope: ReportEnvelope,
  eventsB64: string | null,
): { payload: WirePayload; serialized: string; dropped: boolean } {
  let payload: WirePayload = {
    ...envelope,
    // Transport-level field. Absent when there is no capture to send.
    ...(eventsB64 ? { captureGzipB64: eventsB64 } : {}),
  };
  let serialized = JSON.stringify(payload);
  if (wireBytes(serialized) <= MAX_CLIENT_BODY_BYTES) {
    return { payload, serialized, dropped: false };
  }

  // 1. The recording goes first. It is the largest part by far and the only one
  //    that can be re-obtained — by asking the user to reproduce the problem.
  const capture = { ...envelope.capture, captured: false };
  payload = { ...envelope, capture };
  serialized = JSON.stringify(payload);

  // 2. Then the uncompressed buffers, oldest half at a time. The lines next to
  //    the failure are the ones a triager reads first, so they go last.
  let consoleRows = capture.console;
  let networkRows = capture.network;
  while (
    wireBytes(serialized) > MAX_CLIENT_BODY_BYTES &&
    (consoleRows.length > 0 || networkRows.length > 0)
  ) {
    consoleRows = consoleRows.slice(Math.ceil(consoleRows.length / 2));
    networkRows = networkRows.slice(Math.ceil(networkRows.length / 2));
    payload = { ...envelope, capture: { ...capture, console: consoleRows, network: networkRows } };
    serialized = JSON.stringify(payload);
  }

  // What survives is the user's own text, which the ingest schema caps at 200 +
  // 10 000 characters. There is no report that cannot be delivered as text.
  return { payload, serialized, dropped: true };
}

export function ReportProvider({
  config,
  children,
}: {
  config: ReporterConfig;
  children: React.ReactNode;
}) {
  const [open, setOpenState] = React.useState(false);
  const [mode, setMode] = React.useState<ReporterMode>('report');
  const [lastCaptureDropped, setLastCaptureDropped] = React.useState(false);

  const recorder = useRecorder({
    disabled: config.captureDisabled,
    bufferMs: config.bufferMs,
    longWindow: mode === 'studio',
  });

  const { locale, t } = React.useMemo(() => makeTranslator(config.locale), [config.locale]);

  const setOpen = React.useCallback((next: boolean, nextMode?: ReporterMode) => {
    if (nextMode) setMode(nextMode);
    setOpenState(next);
  }, []);

  const submit = React.useCallback(
    async (body: ReportBody): Promise<SubmitResult> => {
      setLastCaptureDropped(false);

      const capture = recorder.collect();

      // Compress the events out-of-band from the rest of the envelope so a
      // capture that is too large costs us the recording, not the report.
      let eventsB64: string | null = null;
      if (capture.events.length > 0) {
        eventsB64 = await gzipToBase64(JSON.stringify(capture.events));
        if (eventsB64 === null) setLastCaptureDropped(true);
      }

      const identity = {
        surfaceKey: config.surfaceKey,
        appVersion: config.appVersion,
        route: currentRoute(),
        ...(config.getIdentity?.() ?? {}),
      };

      const envelope: ReportEnvelope = {
        schemaVersion: SCHEMA_VERSION,
        generatedAt: new Date().toISOString(),
        mode,
        identity,
        client: {
          userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
          viewport: {
            width: typeof window !== 'undefined' ? window.innerWidth : 0,
            height: typeof window !== 'undefined' ? window.innerHeight : 0,
          },
          locale,
          timeZone: (() => {
            try {
              return Intl.DateTimeFormat().resolvedOptions().timeZone;
            } catch {
              return undefined;
            }
          })(),
          prefersReducedMotion:
            typeof matchMedia !== 'undefined'
              ? matchMedia('(prefers-reduced-motion: reduce)').matches
              : undefined,
          colorScheme:
            typeof document !== 'undefined'
              ? ((document.documentElement.getAttribute('data-theme') as 'light' | 'dark' | null) ??
                undefined)
              : undefined,
        },
        report: body,
        capture: {
          // The raw array is replaced by the compressed blob on the wire; the
          // server rehydrates. Keeping the field named `events` means the
          // envelope type describes both sides honestly.
          events: [],
          console: capture.console,
          network: capture.network,
          durationMs: capture.durationMs,
          captured: capture.captured && eventsB64 !== null,
          masked: capture.masked,
        },
      };

      // The ceiling is enforced on the thing that actually travels, not on an
      // estimate of it. Anything shed here sets `lastCaptureDropped`, which the
      // dialog renders — degrading is allowed, degrading silently is not.
      const { payload, serialized, dropped } = enforceBodyCeiling(envelope, eventsB64);
      if (dropped) setLastCaptureDropped(true);

      if (config.transport) {
        return config.transport(payload);
      }

      try {
        const res = await fetch(config.endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: serialized,
          credentials: 'omit',
        });
        if (!res.ok) {
          let detail = `HTTP ${res.status}`;
          try {
            const j = (await res.json()) as { error?: string; message?: string };
            detail = j.error || j.message || detail;
          } catch {
            /* keep the status */
          }
          return { ok: false, error: detail };
        }
        const j = (await res.json().catch(() => ({}))) as { ticket_ref?: string };
      // A20 SECURITY/HONESTY FIX: propagate `queued`.
      // report-submit answers 202 {accepted:true, queued:true, ticket_ref:null}
      // when the licence server is unreachable and the report was parked in
      // report_outbox. Dropping that flag here made the dialog render
      // "Thank you — your report was received" for a report that had NOT been
      // received — the exact class of lie A20 exists to remove from Support.tsx.
      // The honest string already exists ('result.queued'); it was unreachable.
        if ((j as { queued?: boolean }).queued) return { ok: true, queued: true };
        return { ok: true, ticketRef: j.ticket_ref };
      } catch (err) {
        return { ok: false, error: err instanceof Error ? err.message : 'network error' };
      }
    },
    [config, locale, mode, recorder],
  );

  const value = React.useMemo<ReporterContextValue>(
    () => ({ config, recorder, locale, t, open, mode, setOpen, submit, lastCaptureDropped }),
    [config, recorder, locale, t, open, mode, setOpen, submit, lastCaptureDropped],
  );

  return <ReporterContext.Provider value={value}>{children}</ReporterContext.Provider>;
}
